import { useState, useRef } from 'react';
import { Plus, Users, CalendarDays, ClipboardList, BarChart3, RefreshCw } from '../icons/wp-icons';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import MitarbeiterVerwaltung from '../components/Settings/MitarbeiterVerwaltung';
import AbwesenheitUebersicht from '../components/Abwesenheit/AbwesenheitUebersicht';
import AbwesenheitKalender from '../components/Abwesenheit/AbwesenheitKalender';
import AbwesenheitAntraege from '../components/Abwesenheit/AbwesenheitAntraege';
import AbwesenheitFormular from '../components/Abwesenheit/AbwesenheitFormular';
import Modal from '../components/Common/Modal';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

function Mitarbeiter() {
  const { user } = useAuth();
  const verwaltungRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();

  // Nur Admins haben Zugriff
  if (user?.rolle !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Tab aus URL oder Standard
  const initialTab = searchParams.get('tab') || 'liste';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingAbwesenheit, setEditingAbwesenheit] = useState(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleCreate = () => {
    if (activeTab === 'liste') {
      verwaltungRef.current?.openCreateModal();
    } else {
      setEditingAbwesenheit(null);
      setIsFormModalOpen(true);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleEdit = (abwesenheit) => {
    setEditingAbwesenheit(abwesenheit);
    setIsFormModalOpen(true);
  };

  const handleFormClose = () => {
    setIsFormModalOpen(false);
    setEditingAbwesenheit(null);
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    setEditingAbwesenheit(null);
    handleRefresh();
  };

  const tabs = [
    { id: 'liste', label: 'Mitarbeiter', icon: Users },
    { id: 'uebersicht', label: 'Übersicht', icon: BarChart3 },
    { id: 'kalender', label: 'Kalender', icon: CalendarDays },
    { id: 'antraege', label: 'Anträge', icon: ClipboardList }
  ];

  const getButtonLabel = () => {
    if (activeTab === 'liste') return { full: 'Neuer Mitarbeiter', short: 'Neu' };
    return { full: 'Neue Abwesenheit', short: 'Neu' };
  };

  const buttonLabel = getButtonLabel();

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <div className="flex items-center gap-3">
              <Users className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900">Mitarbeiter</h1>
            </div>
            <p className="text-gray-600 text-sm sm:text-base mt-1">
              {activeTab === 'liste'
                ? 'Mitarbeiterkonten und Zugriffsrechte verwalten'
                : 'Abwesenheiten und Urlaubsplanung'
              }
            </p>
          </div>

          <div className="flex items-center gap-2">
            {activeTab !== 'liste' && (
              <button
                onClick={handleRefresh}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Aktualisieren"
              >
                <RefreshCw className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">{buttonLabel.full}</span>
              <span className="sm:hidden">{buttonLabel.short}</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-surface rounded-card shadow-card mb-6 overflow-hidden">
          <nav className="flex overflow-x-auto scrollbar-hide border-b border-border">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'liste' && (
          <MitarbeiterVerwaltung ref={verwaltungRef} showHeader={false} key={`liste-${refreshKey}`} />
        )}
        {activeTab === 'uebersicht' && (
          <AbwesenheitUebersicht key={`uebersicht-${refreshKey}`} />
        )}
        {activeTab === 'kalender' && (
          <AbwesenheitKalender key={`kalender-${refreshKey}`} onEdit={handleEdit} />
        )}
        {activeTab === 'antraege' && (
          <AbwesenheitAntraege key={`antraege-${refreshKey}`} onRefresh={handleRefresh} />
        )}
      </div>

      {/* Formular Modal für Abwesenheit */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={handleFormClose}
        title={editingAbwesenheit ? 'Abwesenheit bearbeiten' : 'Neue Abwesenheit'}
      >
        <AbwesenheitFormular
          abwesenheit={editingAbwesenheit}
          onSuccess={handleFormSuccess}
          onCancel={handleFormClose}
        />
      </Modal>
    </Layout>
  );
}

export default Mitarbeiter;
